import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserProfile, updateUserEmail } from '../../services/profileService';
import { updateCompanyInfo, uploadCompanyLogo, removeCompanyLogo } from '../../services/companyService';
import { ROLES } from '../../config/constants';
import { dpsTheme } from '../../styles/dpsTheme';
import toast from 'react-hot-toast';

export default function AccountSettings() {
  const { currentUser, userProfile, refreshProfile } = useAuth();

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [isSavingIdentity, setIsSavingIdentity] = useState(false);

  // Company branding
  const [companyName, setCompanyName] = useState('');
  const [companyContactEmail, setCompanyContactEmail] = useState('');
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const isStaffRole = userProfile?.role !== ROLES.CLIENT;
  const canEditCompany = userProfile?.role === ROLES.COMPANY_ADMIN || userProfile?.role === ROLES.SUPER_ADMIN;

  useEffect(() => {
    if (userProfile) {
      setEmail(userProfile.email);
      setFirstName(userProfile.first_name ?? '');
      setLastName(userProfile.last_name ?? '');
      setCompanyName(userProfile.company_name ?? '');
      setCompanyContactEmail(userProfile.company_contact_email ?? '');
    }
  }, [userProfile]);

  const handleSaveEmail = async () => {
    if (!currentUser || !email.trim()) return;
    try {
      setIsSavingEmail(true);
      await updateUserEmail(currentUser.id, email.trim());
      toast.success('Un email de confirmation a été envoyé à votre nouvelle adresse.');
      await refreshProfile();
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la mise à jour de l\'email');
    } finally {
      setIsSavingEmail(false);
    }
  };

  const handleSaveIdentity = async () => {
    if (!currentUser) return;
    try {
      setIsSavingIdentity(true);
      await updateUserProfile(currentUser.id, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      toast.success('Profil mis à jour');
      await refreshProfile();
    } catch {
      toast.error('Erreur lors de la mise à jour du profil');
    } finally {
      setIsSavingIdentity(false);
    }
  };

  const handleSaveCompany = async () => {
    if (!userProfile?.company_id || !companyName.trim()) return;
    try {
      setIsSavingCompany(true);
      await updateCompanyInfo(userProfile.company_id, {
        name: companyName.trim(),
        contact_email: companyContactEmail.trim() || undefined,
      });
      toast.success('Informations société mises à jour');
      await refreshProfile();
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la mise à jour');
    } finally {
      setIsSavingCompany(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userProfile?.company_id) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Fichier invalide — image requise (PNG, JPG, SVG)');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image trop lourde — 2 Mo maximum');
      return;
    }
    try {
      setIsUploadingLogo(true);
      await uploadCompanyLogo(userProfile.company_id, file);
      toast.success('Logo mis à jour');
      await refreshProfile();
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de l\'upload du logo');
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!userProfile?.company_id) return;
    try {
      setIsUploadingLogo(true);
      await removeCompanyLogo(userProfile.company_id);
      toast.success('Logo supprimé');
      await refreshProfile();
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la suppression du logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  if (!userProfile) {
    return (
      <div style={{
        minHeight: '100vh',
        background: dpsTheme.colors.background.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <p style={{ color: dpsTheme.colors.text.muted }}>Chargement...</p>
      </div>
    );
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: dpsTheme.borderRadius.sm,
    border: `1px solid ${dpsTheme.colors.border.primary}`,
    background: dpsTheme.colors.background.darker,
    color: dpsTheme.colors.text.primary,
    fontSize: dpsTheme.typography.fontSize.base,
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: dpsTheme.typography.fontSize.sm,
    fontWeight: dpsTheme.typography.fontWeight.semibold,
    color: dpsTheme.colors.text.secondary,
    marginBottom: '6px',
  };

  const sectionStyle: React.CSSProperties = {
    background: dpsTheme.colors.background.card,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${dpsTheme.colors.border.primary}`,
    borderRadius: dpsTheme.borderRadius.xl,
    padding: '28px 32px',
    marginBottom: '24px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: dpsTheme.typography.fontSize.lg,
    fontWeight: dpsTheme.typography.fontWeight.semibold,
    color: dpsTheme.colors.text.primary,
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: `1px solid ${dpsTheme.colors.border.primary}`,
  };

  const btnPrimaryStyle: React.CSSProperties = {
    padding: '10px 24px',
    borderRadius: dpsTheme.borderRadius.sm,
    border: 'none',
    background: dpsTheme.colors.accent.blue.gradient,
    color: 'white',
    fontSize: dpsTheme.typography.fontSize.sm,
    fontWeight: dpsTheme.typography.fontWeight.semibold,
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: dpsTheme.colors.background.primary,
      fontFamily: dpsTheme.typography.fontFamily,
      paddingTop: '88px',
      paddingBottom: '60px',
    }}>
      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '0 20px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: dpsTheme.typography.fontSize['3xl'],
            fontWeight: dpsTheme.typography.fontWeight.bold,
            color: dpsTheme.colors.text.primary,
            marginBottom: '8px',
          }}>
            Paramètres du compte
          </h1>
          <p style={{ color: dpsTheme.colors.text.muted, fontSize: dpsTheme.typography.fontSize.sm }}>
            {userProfile.company_name} · {userProfile.role}
          </p>
        </div>

        {/* Section Identité — staff uniquement */}
        {isStaffRole && (
          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Identité</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={labelStyle}>Prénom</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Nom</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
            <button
              onClick={handleSaveIdentity}
              disabled={isSavingIdentity}
              style={{ ...btnPrimaryStyle, opacity: isSavingIdentity ? 0.6 : 1 }}
            >
              {isSavingIdentity ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        )}

        {/* Section Ma société — company_admin et super_admin uniquement */}
        {canEditCompany && (
          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Ma société</h2>

            {/* Logo */}
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Logo</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
                {userProfile.company_logo_url ? (
                  <img
                    src={userProfile.company_logo_url}
                    alt="Logo"
                    style={{ height: '48px', width: 'auto', objectFit: 'contain', borderRadius: '6px', border: `1px solid ${dpsTheme.colors.border.secondary}`, padding: '4px', background: 'rgba(255,255,255,0.05)' }}
                  />
                ) : (
                  <div style={{ width: '48px', height: '48px', borderRadius: '6px', background: dpsTheme.colors.accent.blue.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleLogoUpload}
                  />
                  <button
                    onClick={() => logoInputRef.current?.click()}
                    disabled={isUploadingLogo}
                    style={{ ...btnPrimaryStyle, opacity: isUploadingLogo ? 0.6 : 1, fontSize: '13px', padding: '8px 16px' }}
                  >
                    {isUploadingLogo ? 'Upload...' : userProfile.company_logo_url ? 'Changer' : 'Ajouter un logo'}
                  </button>
                  {userProfile.company_logo_url && (
                    <button
                      onClick={handleRemoveLogo}
                      disabled={isUploadingLogo}
                      style={{ padding: '8px 16px', borderRadius: dpsTheme.borderRadius.sm, border: `1px solid rgba(239,68,68,0.4)`, background: 'rgba(239,68,68,0.1)', color: '#f87171', fontSize: '13px', fontWeight: 500, cursor: 'pointer', opacity: isUploadingLogo ? 0.5 : 1 }}
                    >
                      Supprimer
                    </button>
                  )}
                </div>
              </div>
              <p style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted }}>
                PNG, JPG ou SVG — 2 Mo max. Affiché dans la navbar et les PDFs.
              </p>
            </div>

            {/* Nom société */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Nom de la société</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                style={{ ...inputStyle, marginBottom: '4px' }}
                placeholder="Ex: Mon Cabinet de Sécurité"
              />
            </div>

            {/* Email de contact */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Email de contact <span style={{ color: dpsTheme.colors.text.muted, fontWeight: 400 }}>(affiché dans le footer)</span></label>
              <input
                type="email"
                value={companyContactEmail}
                onChange={(e) => setCompanyContactEmail(e.target.value)}
                style={{ ...inputStyle, marginBottom: '4px' }}
                placeholder="contact@masociete.fr"
              />
            </div>

            <button
              onClick={handleSaveCompany}
              disabled={
                isSavingCompany ||
                !companyName.trim() ||
                (companyName === userProfile.company_name &&
                  companyContactEmail === (userProfile.company_contact_email ?? ''))
              }
              style={{
                ...btnPrimaryStyle,
                opacity: (
                  isSavingCompany ||
                  !companyName.trim() ||
                  (companyName === userProfile.company_name &&
                    companyContactEmail === (userProfile.company_contact_email ?? ''))
                ) ? 0.5 : 1,
                cursor: (
                  isSavingCompany ||
                  !companyName.trim() ||
                  (companyName === userProfile.company_name &&
                    companyContactEmail === (userProfile.company_contact_email ?? ''))
                ) ? 'not-allowed' : 'pointer',
              }}
            >
              {isSavingCompany ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        )}

        {/* Section Connexion — tous les rôles */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Connexion</h2>

          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>Adresse email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ ...inputStyle, marginBottom: '12px' }}
            />
            <p style={{
              fontSize: dpsTheme.typography.fontSize.xs,
              color: dpsTheme.colors.text.muted,
              marginBottom: '12px',
            }}>
              Un email de confirmation sera envoyé à la nouvelle adresse avant que le changement ne soit effectif.
            </p>
            <button
              onClick={handleSaveEmail}
              disabled={isSavingEmail || email === userProfile.email}
              style={{
                ...btnPrimaryStyle,
                opacity: (isSavingEmail || email === userProfile.email) ? 0.5 : 1,
                cursor: (isSavingEmail || email === userProfile.email) ? 'not-allowed' : 'pointer',
              }}
            >
              {isSavingEmail ? 'Enregistrement...' : 'Changer l\'email'}
            </button>
          </div>

          <div style={{
            paddingTop: '20px',
            borderTop: `1px solid ${dpsTheme.colors.border.secondary}`,
          }}>
            <label style={labelStyle}>Mot de passe</label>
            <p style={{
              fontSize: dpsTheme.typography.fontSize.sm,
              color: dpsTheme.colors.text.muted,
              marginBottom: '12px',
            }}>
              Pour changer votre mot de passe, vous serez redirigé vers la page dédiée.
            </p>
            <Link
              to="/update-password"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 24px',
                borderRadius: dpsTheme.borderRadius.sm,
                border: `1px solid ${dpsTheme.colors.border.primary}`,
                background: 'rgba(255,255,255,0.05)',
                color: dpsTheme.colors.text.secondary,
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                textDecoration: 'none',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
            >
              Changer le mot de passe
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
